import {useRef, useEffect} from 'react';

import styled from 'styled-components';

import {ICoach} from '@stryd/models';
import {colors, dimensions} from '@stryd/react-ui';

import {withBasePath} from 'src/utils/router';

import {platformOptions} from '../coaches-page';

const CoachesProfile = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: ${colors.themes.dark.backgroundInverted};
  min-height: 100%;
  font-size: 16px;
`;

interface PortraitProps {
  imgURL: string;
}

const Portrait = styled.div<PortraitProps>`
  width: 100%;
  position: relative;
  background-image: url(${(props) => props.imgURL});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  padding: 50% 0;
`;

const DarkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
`;

const OverlayInfo = styled.div`
  position: absolute;
  z-index: 1;
  top: 15px;
  left: 20px;
  text-transform: capitalize;
  color: ${colors.themes.dark.buttonPrimaryText};
`;

const Name = styled.h3`
  display: flex;
  color: ${colors.themes.dark.buttonPrimaryText};
  margin-bottom: 5px;
  text-transform: capitalize;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-start;
  & div {
    margin-right: 0.2em;
  }
  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    flex-flow: column nowrap;
    align-items: flex-start;
    & div {
      margin-right: 0;
    }
  }
`;

const SocialIcons = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 20px;
  right: 15px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  color: ${colors.themes.dark.buttonPrimaryText};
`;

const SocialLink = styled.a`
  margin-right: 15px;
  border-radius: 5px;
  cursor: pointer;
  &:last-of-type {
    margin-right: 0;
  }
`;

const SocialIcon = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 5px;
  display: flex;
`;

const PlatformIcon = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 5px;
  display: flex;
  margin-right: 5px;
  &:last-of-type {
    margin-right: 0;
  }
`;

const ExitButton = styled.img`
  position: absolute;
  top: 15px;
  right: 15px;
  height: 30px;
  opacity: 0.75;
  &:hover {
    opacity: 1;
    cursor: pointer;
  }
`;

const Info = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  color: ${colors.themes.dark.textInverted};
  margin: 20px 20px;
  line-height: 1.2em;
`;

const Bio = styled.p``;

const DataEntry = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 15px;
`;

const DataLabel = styled.h6`
  margin-right: 10px;
  font-weight: bold;
  margin-bottom: 5px;
  margin-top: 0;
`;

const DataValue = styled.span`
  display: flex;
  flex-flow: row nowrap;
`;

type SelectedCoachProps = {
  coachData: ICoach;
  handleExitClick: () => void;
};

const SelectedCoach: React.FC<React.PropsWithChildren<SelectedCoachProps>> = ({
  coachData,
  handleExitClick,
}) => {
  const {
    thumbnail,
    prefix,
    first_name,
    middle_name,
    last_name,
    languages,
    bio,
    email,
    website,
    twitter,
    instagram,
    facebook,
    youtube,
    training_platforms,
    other_platforms,
  } = coachData;

  const coachRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (coachRef.current && coachRef.current.parentElement) {
      coachRef.current.parentElement.scrollTo(0, 0);
    }
  }, [email]);

  return first_name ? (
    <CoachesProfile ref={coachRef}>
      <OverlayInfo>
        <Name>
          <div>
            {prefix ? `${prefix} ` : ''}
            {first_name}
          </div>
          {middle_name && <div>{middle_name}</div>}
          <div>{last_name}</div>
        </Name>
        <DataEntry>
          <DataValue>{languages.join(', ')}</DataValue>
        </DataEntry>
      </OverlayInfo>
      <Portrait imgURL={thumbnail}>
        <DarkOverlay />
        <SocialIcons>
          {facebook.length > 0 && (
            <SocialLink href={facebook} target="_blank" rel="noreferrer">
              <SocialIcon
                src={withBasePath('/images/social-icons/facebook.png')}
                alt="Facebook"
              ></SocialIcon>
            </SocialLink>
          )}
          {twitter.length > 0 && (
            <SocialLink href={twitter} target="_blank" rel="noreferrer">
              <SocialIcon
                src={withBasePath('/images/social-icons/twitter.png')}
                alt="Twitter"
              ></SocialIcon>
            </SocialLink>
          )}
          {instagram.length > 0 && (
            <SocialLink href={instagram} target="_blank" rel="noreferrer">
              <SocialIcon
                src={withBasePath('/images/social-icons/instagram.png')}
                alt="Instagram"
              ></SocialIcon>
            </SocialLink>
          )}
          {youtube.length > 0 && (
            <SocialLink href={youtube} target="_blank" rel="noreferrer">
              <SocialIcon
                src={withBasePath('/images/social-icons/youtube.png')}
                alt="YouTube"
              ></SocialIcon>
            </SocialLink>
          )}
        </SocialIcons>
        <ExitButton
          onClick={handleExitClick}
          src={withBasePath('/icons/icon-close.svg')}
        />
      </Portrait>

      <Info>
        <Bio>{bio}</Bio>
        {website.length > 0 && (
          <DataEntry>
            <DataLabel>Website:</DataLabel>
            <a href={website} target="_blank" rel="noreferrer">
              <DataValue>{website}</DataValue>
            </a>
          </DataEntry>
        )}
        <DataEntry>
          <DataLabel>Email:</DataLabel>
          <a href={`mailto:${email}`}>
            <DataValue>{email}</DataValue>
          </a>
        </DataEntry>
        <DataEntry>
          <DataLabel>Training Platforms:</DataLabel>
          <DataValue>
            {training_platforms.map((platform) => {
              switch (platform) {
                case platformOptions.two_peak: {
                  return (
                    <PlatformIcon
                      key={`coach_${email}_2peak`}
                      src={withBasePath('/icons/platform/icon-2peak.png')}
                      alt={'2 Peaks'}
                    />
                  );
                }
                case platformOptions.training_peaks: {
                  return (
                    <PlatformIcon
                      key={`coach_${email}_training_peaks`}
                      src={withBasePath(
                        '/icons/platform/icon-training_peaks.png'
                      )}
                      alt={'Training Peaks'}
                    />
                  );
                }
                case platformOptions.final_surge: {
                  return (
                    <PlatformIcon
                      key={`coach_${email}_final_surge`}
                      src={withBasePath('/icons/platform/icon-final_surge.png')}
                      alt={'Final Surge'}
                    />
                  );
                }
                case platformOptions.power_center: {
                  return (
                    <PlatformIcon
                      key={`coach_${email}_power_center`}
                      src={withBasePath(
                        '/icons/platform/icon-power_center.png'
                      )}
                      alt={'Stryd PowerCenter'}
                    />
                  );
                }
                default: {
                  return <></>;
                }
              }
            })}
          </DataValue>
        </DataEntry>
        {other_platforms && (
          <DataEntry>
            <DataLabel>Other Platforms:</DataLabel>
            <DataValue>{other_platforms}</DataValue>
          </DataEntry>
        )}
      </Info>
    </CoachesProfile>
  ) : (
    <></>
  );
};

export default SelectedCoach;
